<template lang="pug">
#event-reply-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span イベント参加申込み情報修正
    .box-body.register-body
      FormLabel(key-name="利用者名（部屋名）" :value="residentName")
      FormLabel(key-name="予約家族名（続柄）" :value="familyName")

      .attendees
        .attendees-title 参加者情報
        .attendees-note
          span イベント参加者を一名ずつ入力してください。
          br
          span 追加ボタンを押すと入力欄を増やすことが出来ます。
        .attendees-members
          .d-flex.flex-row.justify-content-start.mb-2(v-for="(attendee, index) in eventReply.attendees")
            .attendee-name.mr-2
              b-form-input(v-model="attendee.name " placeholder="参加者名" :name="'attendee-name-' + index" :maxlength="32"
                v-validate="{ required: true, max: 32 }" :data-vv-as="(index + 1) + '人目の参加者名'")
            .attendee-type.mr-2
              b-form-select.attendee-type-select(v-model="attendee.attendanceType" :options="attendanceTypeOptions" :name="'attendee-type-' + index"
                v-validate="{ required: true }" :data-vv-as="(index + 1) + '人目の参加種別'")
            .attendee-delete
              button.btn.attendee-delete-btn(@click="onAttendeeDelete(index)" type="button") 削除
        .add-attendee
          .d-flex.flex-row.justify-content-end
            button.btn.add-attendee-btn(@click="onAddAttendee()" type="button") 追加

        .error-messages-contents(v-for="(attendee, index) in eventReply.attendees")
          .error-messages(v-for="error in errors.collect('attendee-name-' + index)")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('attendee-type-' + index)")
            .error-message {{ error }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoEventList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="イベント申込み情報を更新しますか？" @ok="updateEventReply")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoEventList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import EventRepliesService, { AttendType, EventRepliesGetModel, EventRepliesPathModel } from "@/commons/services/EventReplies";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import EventsService, { EventGetModel, EventHoldType } from "@/commons/services/Events";
import SystemError from "@/commons/models/SystemError";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * イベント返信編集画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    FormLabel
  },
  $_veeValidate: { validator: "new" }
})
export default class EventReplyEdit extends Vue {
  /** イベント返信サービス */
  @Inject("EventRepliesService") eventRepliesService!: EventRepliesService;
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** イベントサービス */
  @Inject("EventsService") eventsService!: EventsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 利用者名（部屋名） */
  residentName: string = "";
  /** 予約家族名（続柄） */
  familyName: string = "";
  /** イベント返信 */
  eventReply: EventRepliesGetModel | any = {
    attendees: []
  };
  /** イベント参加種別の選択肢 */
  attendanceTypeOptions: Array<{ value: AttendType; text: string }> = [];
  /** 画面表示時に取得したイベント */
  event: EventGetModel | null = null;

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;
  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 返信元のイベントから参加方法の選択肢を作成する
   * - 編集対象のイベント返信を取得する
   * - 画面表示情報のために家族情報を取得する
   *   - 家族情報には利用者情報も含まれるので、家族情報を取得するのみ
   * - 家族情報から画面表示情報を作成する
   *   - 利用者名（部屋名）
   *   - 家族名（続柄）
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const parentEventsSortKey: string = this.$route.params.id;
    const eventId: number = Number(parentEventsSortKey.split("_")[0]);
    const event: EventGetModel = await this.getEvent(eventId);
    this.event = event;

    const attendanceTypeOptions: Array<{ value: AttendType; text: string }> = this.createAttendanceTypeOptions(event.holdType);
    this.attendanceTypeOptions = attendanceTypeOptions;

    const eventRepliesSortKey: string = this.$route.params.id;
    const eventReply: EventRepliesGetModel = await this.getEventReply(eventRepliesSortKey);
    const adjustedEventReply: EventRepliesGetModel = this.adjustmentEventReply(eventReply, event.holdType);
    this.eventReply = adjustedEventReply;

    const residentId: number = Number(eventReply.residentId);
    const familyId: number = Number(eventReply.familyId);
    const family: FamilyGetModel | undefined = await this.getFamily(residentId, familyId);

    if (family != undefined) {
      this.residentName = `${family.name}（${family.room}）`;
      this.familyName = `${family.familyName}（${family.familyRelation}）`;
    } else {
      this.residentName = "";
      this.familyName = "";
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 返信元のイベント取得
   */
  public async getEvent(eventId: number): Promise<EventGetModel> {
    const response: EventGetModel = await this.eventsService.get(userStore, eventId);
    return response;
  }

  /**
   * ### 参加方法の選択肢を作成する
   *
   * - 現地開催イベントは、「現地参加」のみ選択可能
   * - オンライン開催イベントは、「オンライン参加」のみ選択可能
   * - 現地とオンライン開催イベントは、「現地参加」と「オンライン参加」を選択可能
   */
  public createAttendanceTypeOptions(holdType: EventHoldType): Array<{ value: AttendType; text: string }> {
    switch (holdType) {
      case EventHoldType.LOCAL_ONLY: {
        return [{ value: AttendType.LOCAL, text: "現地参加" }];
      }
      case EventHoldType.ONLINE_ONLY: {
        return [{ value: AttendType.ONLINE, text: "オンライン参加" }];
      }
      case EventHoldType.LOCAL_AND_ONLINE: {
        return [
          { value: AttendType.LOCAL, text: "現地参加" },
          { value: AttendType.ONLINE, text: "オンライン参加" }
        ];
      }
      default: {
        return [];
      }
    }
  }

  /**
   * ### 申込状況を取得する
   */
  public async getEventReply(eventRepliesSortKey: string): Promise<EventRepliesGetModel> {
    const eventReply: EventRepliesGetModel = await this.eventRepliesService.get(userStore, eventRepliesSortKey);
    return eventReply;
  }

  /**
   * ### イベント返信にひもづく参加者の参加方法を調整する
   *
   * - イベントの開催方法に応じて参加方法を調整する
   *   - 現地開催イベントは、「現地参加」にする
   *   - オンライン開催イベントは、「オンライン参加」にする
   */
  public adjustmentEventReply(eventReply: EventRepliesGetModel, holdType: EventHoldType): EventRepliesGetModel {
    for (const attendee of eventReply.attendees) {
      switch (holdType) {
        case EventHoldType.LOCAL_ONLY: {
          attendee.attendanceType = AttendType.LOCAL;
          break;
        }
        case EventHoldType.ONLINE_ONLY: {
          attendee.attendanceType = AttendType.ONLINE;
          break;
        }
      }
    }

    return eventReply;
  }

  /**
   * ### 家族を取得する
   */
  public async getFamily(residentId: number, familyId: number): Promise<FamilyGetModel | undefined> {
    const response: Array<FamilyGetModel> = await this.familiesService.getsInResident(userStore, residentId);

    for (const family of response) {
      if (family.familyId == familyId) {
        return family;
      }
    }

    return undefined;
  }

  /**
   * ### 削除ボタン押下
   */
  public onAttendeeDelete(index: number): void {
    if (1 < this.eventReply.attendees.length) {
      this.eventReply.attendees.splice(index, 1);
    } else {
      this.eventReply.attendees.splice(index, 1);
      this.eventReply.attendees.push({ name: "", attendanceType: undefined });
    }

    return;
  }

  /**
   * ### 参加者追加ボタン押下
   */
  public onAddAttendee(): void {
    this.eventReply.attendees.push({
      name: "",
      attendanceType: undefined
    });
    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### イベント返信を更新する
   */
  public async updateEventReply(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: EventRepliesPathModel = this.createRequestBody();
      const eventRepliesSortKey: string = this.$route.params.id;
      await this.updateEventReplies(eventRepliesSortKey, requestBody);

      // 登録成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### イベント返信のリクエストボディを作成する
   */
  public createRequestBody(): EventRepliesPathModel {
    const requestBody: EventRepliesPathModel = {
      attendees: this.eventReply.attendees,
      updated: this.eventReply.updated
    };
    return requestBody;
  }

  /**
   * ### イベント返信を更新する
   */
  public async updateEventReplies(eventRepliesSortKey: string, requestBody: EventRepliesPathModel): Promise<void> {
    await this.eventRepliesService.patch(userStore, eventRepliesSortKey, requestBody);
    return;
  }

  /**
   * ### イベント一覧に遷移する
   */
  public gotoEventList(): void {
    this.$router.push(PcUrl.EventList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#event-reply-edit {
  max-width: 530px;
  margin-left: auto;
  margin-right: auto;

  .attendees {
    margin-left: 20px;
    width: 468px;
    max-width: 468px;

    .attendees-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 4px;
    }

    .attendees-note {
      margin-bottom: 4px;
    }

    .attendee-type-select {
      width: 154px;
    }

    .attendee-delete-btn,
    .add-attendee-btn {
      width: 60px;
      font-size: 16px !important;
      height: 38px !important;
    }
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
